import React from 'react';
import { EMAIL_SLIDE, PAUSE_SLIDE, PLAN_GRAPH_SLIDE, PLAN_LOADER_SLIDE, PRODUCT_SLIDE, QUESTION_ANSWER_SLIDE_COMPONENT, QUESTION_MULTI_ANSWER_SLIDE_COMPONENT, START_SLIDE } from '../quiz-component-types';
import pauseCollage1Src from '../../assets/images/slide/pause-collage-1-self.png';
import pauseCollage2Src from '../../assets/images/slide/pause-collage-2-self.png';
import pauseCollage3Src from '../../assets/images/slide/pause-collage-3-self.png';
import startImageSelfSrc from '../../assets/images/slide/start-image-memory-self.png';

const PLAN_TOPIC_ANSWERS = [
    {
        text: 'Childhood',
        icon: 'house',
        id: 1
    },
    {
        text: 'School years',
        icon: 'backpack',
        id: 2
    },
    {
        text: 'Your twenties',
        icon: 'bottle',
        id: 3
    },
    {
        text: 'Love',
        icon: 'heart-red',
        id: 4
    },
    {
        text: 'The family years',
        icon: 'children',
        id: 5
    },
    {
        text: 'The working years',
        icon: 'calendar',
        id: 6
    },
    {
        text: 'These days',
        icon: 'tree',
        id: 7
    }
];

const QUIZ_SLIDES_V29 = [
    {
        id: 900,
        component: START_SLIDE,
        isNotIndexing: true,
        props: {
            imageSrc: startImageSelfSrc,
            heading: <><em>What happens to your memories</em> if you don&apos;t save them?</>,
            ageQuestion: {
                id: 901,
                category: 'About you',
                question: 'How old are you?',
                answers: [
                    {
                        text: '80+',
                        id: 7
                    },
                    {
                        text: '70–79',
                        id: 6
                    },
                    {
                        text: '60–69',
                        id: 5
                    },
                    {
                        text: 'Under 60',
                        id: 4
                    }
                ]
            }
        }
    },
    {
        id: 902,
        component: QUESTION_ANSWER_SLIDE_COMPONENT,
        props: {
            category: 'About you',
            question: 'How often do you see your family these days?',
            answers: [
                {
                    text: 'Every week',
                    id: 1
                },
                {
                    text: 'Once or twice a month',
                    id: 2
                },
                {
                    text: 'A few times a year',
                    id: 3
                },
                {
                    text: 'Hardly ever',
                    id: 4
                }
            ]
        }
    },
    {
        id: 903,
        component: QUESTION_ANSWER_SLIDE_COMPONENT,
        props: {
            category: 'About you',
            question: 'And when you do — what do you talk about?',
            answers: [
                {
                    text: 'Health, weather, and daily chores',
                    id: 4
                },
                {
                    text: 'Family news and gossip',
                    id: 5
                },
                {
                    text: 'The same few stories on repeat',
                    id: 6
                },
                {
                    text: 'Honestly — just small talk',
                    id: 7
                }
            ]
        }
    },
    {
        id: 9031,
        component: PAUSE_SLIDE,
        isNotIndexing: true,
        continueButton: {
            isVisible: true
        },
        props: {
            imageSrc: pauseCollage1Src,
            titlePrefix: 'They know how your week was.',
            titleAccent: 'Not who you really are.',
            isTitleInline: true,
            description: <>Small talk swallows every visit. Nobody asks who you were before time slips away. <strong>It isn't anyone's fault.</strong></>
        }
    },
    {
        id: 904,
        component: QUESTION_MULTI_ANSWER_SLIDE_COMPONENT,
        continueButton: {
            isVisible: true
        },
        props: {
            category: 'About you',
            question: 'Which of these can you still see, clear as day?',
            description: 'Select everything that instantly comes back to you.',
            answers: [
                {
                    text: 'Your childhood home, room by room',
                    icon: 'childhood-home',
                    id: 1
                },
                {
                    text: 'A song from your youth',
                    icon: 'music-note',
                    id: 2
                },
                {
                    text: 'What you bought with your first paycheck',
                    icon: 'money',
                    id: 3
                },
                {
                    text: 'The day you met the person who mattered',
                    icon: 'white-heart',
                    id: 4
                },
                {
                    text: 'The hardest year you survived',
                    icon: 'rain-cloud',
                    id: 5
                },
                {
                    text: 'All of it, once I start',
                    icon: 'sparkles',
                    id: 6
                }
            ]
        }
    },
    {
        id: 9041,
        component: PAUSE_SLIDE,
        isNotIndexing: true,
        continueButton: {
            isVisible: true
        },
        props: {
            imageSrc: pauseCollage2Src,
            titlePrefix: 'Everything you picked lives',
            titleAccent: 'in one place only.',
            isTitleInline: true,
            description: <>{'It exists only in your memory — nowhere else. '}<strong>An entire lifetime, lost if left unsaid.</strong></>
        }
    },
    {
        id: 905,
        component: QUESTION_ANSWER_SLIDE_COMPONENT,
        props: {
            category: 'About you',
            question: 'What stops you from sharing your story?',
            answers: [
                {
                    text: "I feel like I'm bothering them",
                    icon: 'sad',
                    id: 4
                },
                {
                    text: 'I make it sound like no big deal',
                    icon: 'pinching-hand',
                    id: 5
                },
                {
                    text: 'Everyday life gets in the way — I keep saying "I’ll do it later"',
                    icon: 'hourglass',
                    id: 6
                },
                {
                    text: 'I’m not a writer and I wouldn\'t know where to start',
                    icon: 'question',
                    id: 7
                }
            ]
        }
    },
    {
        id: 906,
        component: QUESTION_ANSWER_SLIDE_COMPONENT,
        props: {
            category: 'About you',
            question: 'Now think about your own mother.',
            description: 'How much of her real story do you actually know?',
            answers: [
                {
                    text: 'A lot — she shared plenty',
                    icon: 'heart',
                    id: 1
                },
                {
                    text: 'Just two or three stories',
                    icon: 'puzzle',
                    id: 2
                },
                {
                    text: 'Just her name and a face',
                    icon: 'sad',
                    id: 3
                },
                {
                    text: 'Almost nothing',
                    icon: 'question',
                    id: 4
                }
            ]
        }
    },
    {
        id: 9061,
        component: PAUSE_SLIDE,
        isNotIndexing: true,
        continueButton: {
            isVisible: true
        },
        props: {
            imageSrc: pauseCollage3Src,
            titlePrefix: 'The stories your mother never recorded',
            titleAccent: 'are gone forever.',
            isTitleInline: true,
            description: <>Your children and grandchildren are facing the exact same <strong>loss with you right now. </strong>You have the power to break that cycle.</>
        }
    },
    {
        id: 907,
        component: QUESTION_MULTI_ANSWER_SLIDE_COMPONENT,
        continueButton: {
            isVisible: true,
            minAnswers: 2
        },
        props: {
            category: 'Personalize your plan',
            question: 'Which chapters does your story need?',
            description: 'Select at least 2',
            answers: PLAN_TOPIC_ANSWERS
        }
    },
    {
        id: 908,
        component: QUESTION_ANSWER_SLIDE_COMPONENT,
        props: {
            category: 'Personalize your plan',
            question: 'And which one feels easiest to start with?',
            answerSourceId: 907,
            answerPool: PLAN_TOPIC_ANSWERS
        }
    },
    {
        id: 9081,
        component: PRODUCT_SLIDE,
        isNotIndexing: true,
        continueButton: {
            isVisible: true
        },
        props: {
            cardQuestion: 'What did you want to be, before real life started?',
            titlePrefix: 'You don\'t have to write a book.',
            titleAccent: 'Just answer one question.',
            description: <>Ask anyone for an autobiography and they freeze. <br /> Ask one simple question — and the memories flood right back.</>
        }
    },
    {
        id: 909,
        component: QUESTION_ANSWER_SLIDE_COMPONENT,
        props: {
            category: 'Personalize your plan',
            question: 'How would you prefer to share your stories?',
            answers: [
                {
                    text: 'Just talking — I’d rather speak than type',
                    icon: 'mic-old',
                    id: 4
                },
                {
                    text: 'Writing — I like to take my time with words',
                    icon: 'writing',
                    id: 5
                },
                {
                    text: 'A bit of both — depends on the day',
                    icon: 'refresh',
                    id: 6
                }
            ]
        }
    },
    {
        id: 9091,
        component: PRODUCT_SLIDE,
        isNotIndexing: true,
        continueButton: {
            isVisible: true
        },
        props: {
            variant: 'v2',
            headingPrefix: 'Whichever you choose,',
            headingAccent: 'we handle the hard part.',
            stepOptions: [
                {
                    icon: 'pencil',
                    title: 'You type a few lines',
                    subtitle: 'whenever you have a minute'
                },
                {
                    icon: 'mic',
                    title: 'You press record and talk',
                    subtitle: 'we turn it into writing for you'
                }
            ],
            chapterTitle: 'Your first chapter',
            chapterBullets: ['edited and polished', 'in your own words', 'ready to share']
        }
    },
    {
        id: 9092,
        component: PLAN_GRAPH_SLIDE,
        isNotIndexing: true,
        continueButton: {
            isVisible: true,
            label: 'Start today'
        },
        props: {
            titlePrefix: 'One question at a time,',
            titleAccent: 'your story is saved forever.',
            topics: ['house', 'backpack', 'bottle', 'heart-red', 'children', 'calendar', 'tree']
        }
    },
    {
        id: 910,
        component: QUESTION_MULTI_ANSWER_SLIDE_COMPONENT,
        continueButton: {
            isVisible: true
        },
        props: {
            category: 'Who should be able to read this one day?',
            question: 'Who else should be able to hear this one day?',
            answers: [
                {
                    text: 'My children',
                    icon: 'children',
                    id: 1
                },
                {
                    text: 'My family and siblings',
                    icon: 'siblings',
                    id: 2
                },
                {
                    text: 'Grandchildren — now or in the future',
                    icon: 'baby',
                    id: 3
                },
                {
                    text: 'My partner or spouse',
                    icon: 'heart-red',
                    id: 4
                },
                {
                    text: 'Just me, for now',
                    icon: 'smile',
                    id: 5
                }
            ]
        }
    },
    {
        id: 911,
        component: QUESTION_ANSWER_SLIDE_COMPONENT,
        props: {
            category: 'Personalize your plan',
            question: 'What will you regret most if you keep waiting?',
            answers: [
                {
                    text: 'Leaving my life story blank',
                    icon: 'hourglass',
                    id: 7
                },
                {
                    text: 'Becoming just a photo on the wall',
                    icon: 'silhouette',
                    id: 8
                },
                {
                    text: 'My hard-earned lessons gone forever',
                    icon: 'hushed-face',
                    id: 9
                },
                {
                    text: 'Unanswered questions left behind',
                    icon: 'broken-heart',
                    id: 10
                },
                {
                    text: 'All of the above — I\'m starting today',
                    icon: 'sparkles',
                    id: 11
                }
            ]
        }
    },
    {
        id: 944,
        component: PLAN_LOADER_SLIDE,
        isNotBackButton: true,
        isNotIndexing: true,
        props: {
            steps: ['Reading your answers', 'Preparing your first chapter', 'Matching questions to your chapters', 'First question ready'],
            stats: [
                {
                    id: 1,
                    percent: '4%',
                    description: 'of us can name all eight of our great-grandparents.',
                    highlight: <strong>Leave more than a name.</strong>
                },
                {
                    id: 2,
                    percent: '20',
                    description: 'questions about family history predict how children cope.',
                    highlight: <strong>You hold the answers.</strong>
                },
                {
                    id: 3,
                    percent: '42',
                    description: 'studies found that looking back on purpose lifts mood.',
                    highlight: <strong>It’s good for you, too.</strong>
                }
            ]
        }
    },
    {
        id: 945,
        component: EMAIL_SLIDE,
        isNotBackButton: true,
        isNotIndexing: true,
        props: {
            title: 'Where should we send your first question?',
            description: 'Enter your email — we\'ll set up your account and send your first chapter there.',
            placeholder: 'Enter your email'
        }
    }
];

export {
    QUIZ_SLIDES_V29
};
